import React from 'react';
import { Download } from 'lucide-react';
import { Button } from 'equal-ds-ui';
import { BulkUploadDrawer } from '../../common/bulk-upload';
import type { BulkUploadDrawerProps } from '../../common/bulk-upload';
import type { BulkUploadUserRow } from '../../../types/admin-users';

export interface AdminUsersBulkUploadDrawerProps
  extends Pick<
    BulkUploadDrawerProps<BulkUploadUserRow, BulkUploadUserRow>,
    | 'isOpen'
    | 'onClose'
    | 'fileInput'
    | 'validateConfig'
    | 'isBusy'
    | 'errorSection'
    | 'successSection'
    | 'footerActions'
    | 'contentSlot'
  > {
  onDownloadSample: () => void;
}

/**
 * Admin Users specific wrapper around the generic BulkUploadDrawer.
 * Keeps the parent component lightweight by configuring copy and props.
 */
export const AdminUsersBulkUploadDrawer: React.FC<AdminUsersBulkUploadDrawerProps> = ({
  isOpen,
  onClose,
  onDownloadSample,
  ...drawerProps
}) => {
  if (!isOpen) {
    return null;
  }

  const tipsSection = (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start gap-2 mb-2">
        <span className="text-lg">💡</span>
        <h4 className="text-sm font-semibold text-gray-900">Tips for onboarding</h4>
      </div>
      <p className="text-sm text-gray-600 ml-7 mb-3">
        Ensure the excel sheet you upload has the correct layout. To get started quickly, you can download our template using the button below and add your user details to it.
      </p>
      <div className="ml-7">
        <Button
          variant="secondary"
          size="sm"
          onClick={onDownloadSample}
          className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
        >
          <Download className="w-4 h-4" />
          <span>Download Template</span>
        </Button>
      </div>
    </div>
  );

  return (
    <BulkUploadDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Onboard Users"
      description="Onboard up to 20 users at a time using the upload option."
      sampleFile={{
        label: 'Sample',
        onDownload: onDownloadSample,
      }}
      fileInput={drawerProps.fileInput}
      validateConfig={drawerProps.validateConfig}
      isBusy={drawerProps.isBusy}
      errorSection={drawerProps.errorSection}
      successSection={drawerProps.successSection}
      footerActions={drawerProps.footerActions}
      contentSlot={
        <>
          {tipsSection}
          {drawerProps.contentSlot}
        </>
      }
    />
  );
};
